import type { DepartureReasonEnum } from './payroll';

/**
 * The 14 end-of-service departure reasons and their i18n keys, defined ONCE.
 *
 * These lived inside `views/UserProfile/UserPayroll/EndOfService/index.tsx` while the EOS calculate
 * form was their only consumer. CO-670 gives them a second one — the Exit Reasons tab in Lifecycle
 * Settings, where HR maps each of the company's own exit reasons onto one of these categories — so
 * they moved here rather than being retyped. A second copy of this list is a second place for a
 * label and its translation key to drift apart, on a list that decides what people are paid.
 *
 * Keep in step with the backend enum `Mowazaf.Server.Data.Payroll.Entities.DepartureReason`
 * (`EosEnums.cs`). The `value`s are the PascalCase enum names exactly as they travel on the wire
 * (StringEnumConverter), which is also how `ExitReasonView.eosCategory` and
 * `PreviewEosInput.departureReason` are serialised.
 */
export interface DepartureReasonOption {
  value: DepartureReasonEnum;
  /** Flat i18n key in the `common` namespace. Already present in en/ar/fr. */
  labelKey: string;
}

/** The seven generic reasons. None of these is a for-cause ground. */
export const DEPARTURE_REASONS: DepartureReasonOption[] = [
  { value: 'Resignation', labelKey: 'Resignation' },
  { value: 'TerminationWithCause', labelKey: 'Termination With Cause' },
  { value: 'TerminationWithoutCause', labelKey: 'Termination Without Cause' },
  { value: 'MutualAgreement', labelKey: 'Mutual Agreement' },
  { value: 'Redundancy', labelKey: 'Redundancy' },
  { value: 'Retirement', labelKey: 'Retirement' },
  { value: 'DeathOrDisability', labelKey: 'Death or Disability' },
];

/**
 * Named for-cause grounds: UAE FDL 33/2021 Art. 44 sub-reasons (informational only) / KSA Labor Law
 * Art. 80 grounds. Surfacing these named grounds is what makes KSA Art. 80 forfeiture reachable at
 * all — the generic reasons above never map to an Art. 80 ground.
 *
 * Recording one of these on an exit reason does NOT by itself withhold anything: UAE Art. 44 is
 * informational, and KSA Art. 80 forfeiture additionally requires both procedural ticks on the
 * profile EOS page, which no automatic path sets.
 */
export const ART44_SUB_REASONS: DepartureReasonOption[] = [
  { value: 'TerminationArt44_Impersonation', labelKey: 'Impersonation / False Identity' },
  { value: 'TerminationArt44_FaultCausingLoss', labelKey: 'Fault Causing Material Loss' },
  { value: 'TerminationArt44_SafetyViolation', labelKey: 'Safety Violation' },
  { value: 'TerminationArt44_RepeatedMisconduct', labelKey: 'Repeated Misconduct' },
  { value: 'TerminationArt44_AbsenceWithoutExcuse', labelKey: 'Absence Without Excuse' },
  { value: 'TerminationArt44_CompetitorEmployment', labelKey: 'Working for a Competitor' },
  { value: 'TerminationArt44_TradeSecretBreach', labelKey: 'Trade Secret Breach' },
];

/** All 14, in the order the EOS calculate dropdown offers them. */
export const ALL_DEPARTURE_REASONS: DepartureReasonOption[] = [...DEPARTURE_REASONS, ...ART44_SUB_REASONS];

const DEPARTURE_REASON_LABEL_KEYS: Record<string, string> = ALL_DEPARTURE_REASONS.reduce(
  (acc, r) => {
    acc[r.value] = r.labelKey;
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Stored departure reasons persist as PascalCase enum values (e.g. "TerminationWithoutCause"). Any
 * surface showing one must render the same humanized, translatable label the calculate dropdown
 * uses — so map the enum to its label key and run it through `t()`, falling back to a space-split
 * for an unmapped or legacy value.
 */
export const departureReasonLabelKey = (reason: string): string =>
  DEPARTURE_REASON_LABEL_KEYS[reason] ??
  reason.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim();
